import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import SEO from '../components/common/SEO';

const Page = styled.main`
  min-height: calc(100vh - 72px);
  background: linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%);
  padding: 120px 20px 80px;
`;

const Container = styled.section`
  max-width: 960px;
  margin: 0 auto;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 48px 36px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.08);

  @media (max-width: 768px) {
    padding: 32px 20px;
  }
`;

const Eyebrow = styled.p`
  color: #4f46e5;
  font-weight: 700;
  letter-spacing: 0.06em;
  font-size: 0.85rem;
  margin-bottom: 12px;
`;

const Title = styled.h1`
  color: #0f172a;
  font-size: clamp(2rem, 4vw, 2.8rem);
  line-height: 1.2;
  margin-bottom: 16px;
`;

const Description = styled.p`
  color: #475569;
  font-size: 1.05rem;
  line-height: 1.75;
  margin-bottom: 24px;
`;

const List = styled.ul`
  margin: 0 0 28px 0;
  padding-left: 20px;
  color: #334155;
  line-height: 1.8;
`;

const Cta = styled.button`
  border: none;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: #fff;
  padding: 12px 22px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
`;

const Section = styled.section`
  max-width: 960px;
  margin: 20px auto 0;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 36px;

  @media (max-width: 768px) {
    padding: 24px 20px;
  }
`;

const SectionTitle = styled.h2`
  color: #0f172a;
  font-size: clamp(1.4rem, 3vw, 1.9rem);
  margin-bottom: 14px;
`;

const SectionText = styled.p`
  color: #475569;
  line-height: 1.8;
  margin-bottom: 12px;
`;

const FaqList = styled.div`
  display: grid;
  gap: 16px;
`;

const FaqItem = styled.article`
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 16px;
  background: #f8fafc;
`;

const FaqQ = styled.h3`
  color: #1e293b;
  font-size: 1rem;
  margin-bottom: 8px;
`;

const FaqA = styled.p`
  color: #475569;
  line-height: 1.75;
`;

const ResumeGeneratorLandingPage = ({ onLoginClick }) => {
  const navigate = useNavigate();
  const faqs = [
    {
      q: 'AI简历生成器适合哪些人？',
      a: '适合校招生、社招候选人和转行用户。无论你是从零写简历，还是想优化已有简历，都可以用一键生成和智能润色能力快速完成。'
    },
    {
      q: '生成后的简历可以继续编辑吗？',
      a: '可以。你可以在生成后继续补充项目细节、调整描述和切换模板风格，再导出用于投递。'
    },
    {
      q: '和普通在线简历模板有什么区别？',
      a: '除了模板排版，系统会结合岗位语义做内容组织与表达优化，提升关键词匹配度和可读性。'
    },
    {
      q: '是否支持中文简历和英文简历？',
      a: '支持多语言求职场景，中文和英文简历都可以生成并进一步优化。'
    }
  ];

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'SoftwareApplication',
        name: '职唛AI简历生成器',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        url: 'https://zhimai.ai/resume-generator',
        description: '基于职唛AI的一键简历生成能力，快速生成可投递的专业简历。',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'CNY'
        }
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqs.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.a
          }
        }))
      }
    ]
  };

  const handleStart = () => {
    if (onLoginClick) {
      onLoginClick();
      return;
    }
    navigate('/');
  };

  return (
    <>
      <SEO
        title="AI简历生成器 - 一键生成专业简历 | 职唛AI"
        description="职唛AI简历生成器支持一键生成简历、智能排版、在线简历制作、多模板切换与导出，帮助你快速完成高质量简历并提升投递效率。"
        keywords="AI简历生成,简历生成器,在线简历制作,一键生成简历,简历模板,简历优化,英文简历,中文简历,职唛AI简历"
        canonicalUrl="https://zhimai.ai/resume-generator"
        ogImage="https://zhimai.ai/images/og-image.png"
        structuredData={structuredData}
      />
      <Page>
        <Container>
          <Eyebrow>RESUME GENERATOR</Eyebrow>
          <Title>AI 简历生成新功能已上线</Title>
          <Description>
            输入核心经历即可自动生成结构化简历，支持多风格模板、智能优化建议与导出能力，
            让你把时间花在内容价值，而不是排版细节上。
          </Description>
          <List>
            <li>一键生成：从经历信息自动构建完整简历草稿</li>
            <li>智能润色：优化表达和关键词匹配，提升通过率</li>
            <li>模板切换：根据岗位和行业快速更换视觉风格</li>
          </List>
          <Cta onClick={handleStart}>立即体验简历生成</Cta>
        </Container>

        <Section>
          <SectionTitle>为什么选择 AI 在线简历制作</SectionTitle>
          <SectionText>
            与传统简历模板工具不同，AI 简历生成不只是排版，而是结合岗位语义做内容重组，
            帮你把项目成果、业务影响和能力标签表达得更清晰。
          </SectionText>
          <SectionText>
            如果你在找“简历生成器”“在线简历制作”“一键生成简历”这类能力，公开页面会持续更新示例与使用说明，
            方便搜索引擎稳定抓取，也方便候选人在首次访问时快速判断是否适合自己。
          </SectionText>
        </Section>

        <Section>
          <SectionTitle>常见问题</SectionTitle>
          <FaqList>
            {faqs.map((item) => (
              <FaqItem key={item.q}>
                <FaqQ>{item.q}</FaqQ>
                <FaqA>{item.a}</FaqA>
              </FaqItem>
            ))}
          </FaqList>
        </Section>
      </Page>
    </>
  );
};

export default ResumeGeneratorLandingPage;
